import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface ApiError {
  code: number;
  message_code?: string;
  message: string;
  details?: any;
}

export interface ErrorState {
  status: boolean;
  error: ApiError | null;
}

interface ErrorContextType {
  error: ErrorState | null;
  showError: (error: ErrorState) => void;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [error, setError] = useState<ErrorState | null>(null);

  const showError = useCallback((errorState: ErrorState) => {
    setError(errorState);

    // Auto-dismiss after 6 seconds
    setTimeout(() => {
      setError(null);
    }, 6000);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Listen for API error events
  React.useEffect(() => {
    const handleApiError = (event: Event) => {
      const customEvent = event as CustomEvent<ErrorState>;
      if (customEvent.detail) {
        showError(customEvent.detail);
      }
    };

    window.addEventListener('api-error', handleApiError);

    return () => {
      window.removeEventListener('api-error', handleApiError);
    };
  }, [showError]);

  return (
    <ErrorContext.Provider value={{ error, showError, clearError }}>
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};
